import type {
  PathLookupDecisionEvent,
  PathLookupDecisionsTrace,
} from '@ipfabric/utils';
import classNames from 'classnames';
import type { FC } from 'react';

import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { DeviceViewEntityId } from 'components/DeviceView/deviceViewTypes';
import type { CloudConstructKind } from 'components/DeviceView/helpers/cloudConstruct';
import type { DecidingRuleScope } from 'components/SecuritySchema/model/SecuritySchemaEntryPoint';
import { SeverityIcon } from 'components/icons/SeverityIcon';
import { getIconSeverityShapeClassName } from '../getIconSeverityShapeClassName';
import { CloudDecisionCell } from './CloudDecisionCell';
import { CloudSecurityCheckCell } from './CloudSecurityCheckCell';
import { SeverityDetails } from './SeverityDetails';
import {
  isCloudConstructDecisionEvent,
  isCloudSecurityCheckEvent,
} from './cloudDecisionEvent';

/** The cloud constructs whose decision table is this flat one. */
export type FlatCloudConstructKind = Extract<
  CloudConstructKind,
  'networkInterface' | 'peering' | 'subnet'
>;

const emptyRowLabel: { [Kind in FlatCloudConstructKind]: string } = {
  networkInterface: 'No decisions were made here.',
  subnet: 'No decisions were made here.',
  // A peering makes no security or routing decision of its own (NIM-25157),
  // so an empty table is its *normal* cross-VPC hop rather than an unusual one.
  peering: 'Traffic forwarded through the peering.',
};

export const isFlatCloudConstructKind = (
  kind: CloudConstructKind | null,
): kind is FlatCloudConstructKind => kind !== null && kind in emptyRowLabel;

interface Props {
  constructKind: FlatCloudConstructKind;
  deviceViewEntityId: DeviceViewEntityId;
  onShowSecurityCheckDetail: (scope: DecidingRuleScope) => void;
  /**
   * Required: a construct whose chain ran always has a trace object, even when
   * it recorded nothing. A *missing* trace means the construct never ran (e.g.
   * initialisation failed), which is not the same as "decided nothing" — the
   * caller renders `NothingToSeeHere` for that instead of an empty table.
   */
  trace: PathLookupDecisionsTrace;
}

interface RowProps {
  deviceViewEntityId: DeviceViewEntityId;
  event: PathLookupDecisionEvent;
  onShowSecurityCheckDetail: (event: PathLookupDecisionEvent) => void;
}

const headerOperationLabel = (event: PathLookupDecisionEvent): string => {
  const suffix = event.headerType === undefined ? '' : ` ${event.headerType}`;
  if (event.type === 'insert header') {
    return `PUSH${suffix}`;
  }
  if (event.type === 'remove header') {
    return `POP${suffix}`;
  }
  return `PATCH${suffix}`;
};

const getSecurityCheckScope = (
  event: PathLookupDecisionEvent,
): DecidingRuleScope | undefined =>
  event.decidingRule === undefined || event.securityType === undefined
    ? undefined
    : {
        rulePath: event.decidingRule,
        type: event.securityType,
        cloudDirection: event.cloudDirection,
      };

/** Details are rendered inline by `SeverityDetails`, so no tooltip here. */
const SecurityVerdictIcon: FC<{ event: PathLookupDecisionEvent }> = ({
  event,
}) => {
  const { severityInfo } = event;
  if (severityInfo === undefined) {
    return null;
  }
  return (
    <SeverityIcon
      className='mr-lgu-2'
      faIcon={getIconSeverityShapeClassName(event)}
      severity={severityInfo.severity}
    />
  );
};

/**
 * One line of the table. Unlike the on-prem grid there is no location / filter
 * / packet-action split — each decision reads as a single sentence.
 */
const CloudDecisionRow: FC<RowProps> = (props) => {
  const { event } = props;
  if (isCloudConstructDecisionEvent(event)) {
    return (
      <CloudDecisionCell
        deviceViewEntityId={props.deviceViewEntityId}
        event={event}
      />
    );
  }
  if (isCloudSecurityCheckEvent(event)) {
    return (
      <>
        <SecurityVerdictIcon event={event} />
        <CloudSecurityCheckCell
          event={event}
          onShowDetail={() => props.onShowSecurityCheckDetail(event)}
        />
      </>
    );
  }
  if (event.type === 'drop packet') {
    return (
      <>
        <span className='text-red-600'>{`DROP: ${event.reason ?? ''}`}</span>
        <SeverityDetails event={event} />
      </>
    );
  }
  if (event.type === 'accept packet') {
    return (
      <>
        {`ACCEPT: ${event.reason ?? ''}`}
        <SeverityDetails event={event} />
      </>
    );
  }
  if (
    event.type === 'insert header' ||
    event.type === 'remove header' ||
    event.type === 'patch header'
  ) {
    return <>{headerOperationLabel(event)}</>;
  }
  stumble(`Unknown cloud event type ${event.type}`);
  return <>{event.type}</>;
};

const EmptyRow: FC<{ constructKind: FlatCloudConstructKind }> = ({
  constructKind,
}) => <span className='text-gray-500'>{emptyRowLabel[constructKind]}</span>;

/**
 * Decision table of a cloud vNIC, subnet or peering, per the [spec](
 * https://ipfabric.atlassian.net/wiki/spaces/NAE/pages/6267502701) (NIM-25155 /
 * NIM-25156 / NIM-25157): one sentence per decision. A construct with nothing
 * to decide keeps the table and shows its empty row rather than disappearing.
 */
export const CloudDecisionTable: FC<Props> = (props) => {
  const showSecurityCheckDetail = useStableCallback(
    (event: PathLookupDecisionEvent) => {
      const scope = getSecurityCheckScope(event);
      if (scope !== undefined) {
        props.onShowSecurityCheckDetail(scope);
      }
    },
  );
  const rows = props.trace.trace.flatMap((checkpoint, checkpointIndex) =>
    checkpoint.events.map((event, eventIndex) => ({
      event,
      key: `${checkpointIndex}-${eventIndex}`,
    })),
  );
  return (
    <table className='w-full'>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className='px-5 py-4'>
              <EmptyRow constructKind={props.constructKind} />
            </td>
          </tr>
        ) : (
          rows.map(({ event, key }, index) => (
            <tr
              className={classNames(
                index % 2 === 1 && 'pathlookup-tab-content-row--odd',
              )}
              key={key}
            >
              <td className='px-5 py-4'>
                <CloudDecisionRow
                  deviceViewEntityId={props.deviceViewEntityId}
                  event={event}
                  onShowSecurityCheckDetail={showSecurityCheckDetail}
                />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};
